import {
  getSantanderClabes,
  assignSantanderClabe,
  unassignSantanderClabe,
  getSantanderPaymentReports,
  getSantanderPaymentReportDetails,
  reconcileSantanderPayment,
  createUpfrontRent,
  exportReferencedPaymentsReport,
  sendClabeToClient
} from 'src/services/santanderClabeService';
import { asyncActionCreator } from 'src/utils/loadingUtils';
import { SnackBarVariants } from 'src/utils/constants/common';
import { SantanderReportType } from 'src/utils/constants/clients';
import { showMessage } from './snackBarActions';
import { selectSantanderClabeQueryOptions } from '../selectors/santanderClabeSelectors';

export const LOAD_SANTANDER_CLABES = asyncActionCreator(
  'LOAD_SANTANDER_CLABES'
);
export const SAVE_SANTANDER_CLABE_OPTIONS = asyncActionCreator(
  'SAVE_SANTANDER_CLABE_OPTIONS'
);

export const LOAD_SANTANDER_PAYMENT_REPORTS = asyncActionCreator(
  'LOAD_SANTANDER_PAYMENT_REPORTS'
);
export const ASSIGN_SANTANDER_CLABE = asyncActionCreator(
  'ASSIGN_SANTANDER_CLABE'
);
export const UNASSIGN_SANTANDER_CLABE = asyncActionCreator(
  'UNASSIGN_SANTANDER_CLABE'
);

export const LOAD_SANTANDER_PAYMENT_REPORT_DETAILS = asyncActionCreator(
  'LOAD_SANTANDER_PAYMENT_REPORT_DETAILS'
);

export const RECONCILE_SANTANDER_PAYMENT = asyncActionCreator(
  'RECONCILE_SANTANDER_PAYMENT'
);

export const CREATE_UPFRONT_RENT_SANTANDER = asyncActionCreator(
  'CREATE_UPFRONT_RENT_SANTANDER'
);

export const SEND_CLABE_TO_CLIENT = asyncActionCreator('SEND_CLABE_TO_CLIENT');

export const CLEAR_SANTANDER_CLABE_OPTIONS = 'CLEAR_SANTANDER_CLABE_OPTIONS';

export const DOWNLOAD_SANTANDER_REFERENCED_PAYMENTS = asyncActionCreator(
  'DOWNLOAD_SANTANDER_REFERENCED_PAYMENTS'
);

function getOptions(getState, sendLimit = true) {
  const queryOpts = selectSantanderClabeQueryOptions(getState());

  const reqOptions = {};

  if (queryOpts.page > 0) {
    reqOptions.offset =
      queryOpts.page * Number(process.env.REACT_APP_TABLE_SIZE);
  }
  if (sendLimit) {
    reqOptions.limit = queryOpts.limit;
  }
  if (queryOpts.clientName) {
    reqOptions.clientName = queryOpts.clientName;
  }
  if (queryOpts.clabe) {
    reqOptions.clabe = queryOpts.clabe;
  }
  if (queryOpts.status) {
    reqOptions.status = queryOpts.status;
  }

  return reqOptions;
}

export const loadSantanderClabes = (options, sendLimit = true) => async (
  dispatch,
  getState
) => {
  try {
    dispatch({ type: LOAD_SANTANDER_CLABES.start });

    dispatch({
      type: SAVE_SANTANDER_CLABE_OPTIONS,
      options
    });

    const reqOptions = getOptions(getState, sendLimit);

    const data = await getSantanderClabes(reqOptions);

    return dispatch({
      type: LOAD_SANTANDER_CLABES.success,
      data
    });
  } catch (error) {
    dispatch(
      showMessage({
        message: error.message,
        variant: SnackBarVariants.ERROR
      })
    );
    return dispatch({ type: LOAD_SANTANDER_CLABES.failure });
  }
};

function getReportsOptions(getState, sendLimit = true) {
  const queryOpts = selectSantanderClabeQueryOptions(getState());

  const reqOptions = {
    reportType: SantanderReportType.BANK_TRANSFER
  };

  if (queryOpts.page > 0) {
    reqOptions.offset =
      queryOpts.page * Number(process.env.REACT_APP_TABLE_SIZE);
  }
  if (sendLimit) {
    reqOptions.limit = queryOpts.limit;
  }
  if (queryOpts.startDate) {
    reqOptions.startDate = queryOpts.startDate;
  }
  if (queryOpts.endDate) {
    reqOptions.endDate = queryOpts.endDate;
  }
  if (queryOpts.reconciliationStatus) {
    reqOptions.reconciliationStatus = queryOpts.reconciliationStatus;
  }

  return reqOptions;
}

export const loadSantanderPaymentReports = options => async (
  dispatch,
  getState
) => {
  try {
    dispatch({ type: LOAD_SANTANDER_PAYMENT_REPORTS.start });

    dispatch({
      type: SAVE_SANTANDER_CLABE_OPTIONS,
      options
    });

    const reqOptions = getReportsOptions(getState);

    const data = await getSantanderPaymentReports(reqOptions);
    return dispatch({
      type: LOAD_SANTANDER_PAYMENT_REPORTS.success,
      data
    });
  } catch (error) {
    dispatch(
      showMessage({
        message: error.message,
        variant: SnackBarVariants.ERROR
      })
    );
    return dispatch({ type: LOAD_SANTANDER_PAYMENT_REPORTS.failure });
  }
};

export const loadSantanderPaymentReportDetails = ({
  santanderReportId,
  options = {}
}) => async dispatch => {
  try {
    dispatch({ type: LOAD_SANTANDER_PAYMENT_REPORT_DETAILS.start });

    const reqOptions = {
      ...options,
      limit: options.limit,
      page: options.page || 0
    };

    if (reqOptions.page > 0) {
      reqOptions.offset = reqOptions.page * reqOptions.limit;
    }

    const data = await getSantanderPaymentReportDetails({
      santanderReportId,
      options: reqOptions
    });
    return dispatch({
      type: LOAD_SANTANDER_PAYMENT_REPORT_DETAILS.success,
      data
    });
  } catch (error) {
    dispatch(
      showMessage({
        message: error.message,
        variant: SnackBarVariants.ERROR
      })
    );
    return dispatch({ type: LOAD_SANTANDER_PAYMENT_REPORT_DETAILS.failure });
  }
};

export const assignClabe = (
  clabe,
  clientId,
  closeCallback
) => async dispatch => {
  try {
    dispatch({ type: ASSIGN_SANTANDER_CLABE.start });

    await assignSantanderClabe({ clabe, clientId });

    dispatch(
      showMessage({
        message: 'ASSIGN_SANTANDER_CLABE',
        variant: SnackBarVariants.SUCCESS
      })
    );

    dispatch(loadSantanderClabes());
    closeCallback();

    return dispatch({ type: ASSIGN_SANTANDER_CLABE.success });
  } catch (error) {
    dispatch(
      showMessage({
        message: error.message,
        variant: SnackBarVariants.ERROR
      })
    );
    return dispatch({ type: ASSIGN_SANTANDER_CLABE.failure });
  }
};

export const unassignClabe = (clabe, closeCallback) => async dispatch => {
  try {
    dispatch({ type: UNASSIGN_SANTANDER_CLABE.start });

    await unassignSantanderClabe({ clabe });

    dispatch(
      showMessage({
        message: 'UNASSIGN_SANTANDER_CLABE',
        variant: SnackBarVariants.SUCCESS
      })
    );

    dispatch(loadSantanderClabes());
    closeCallback();

    return dispatch({ type: UNASSIGN_SANTANDER_CLABE.success });
  } catch (error) {
    dispatch(
      showMessage({
        message: error.message,
        variant: SnackBarVariants.ERROR
      })
    );
    return dispatch({ type: UNASSIGN_SANTANDER_CLABE.failure });
  }
};

export const reconcileSantanderPaymentAction = (
  paymentId,
  reconcileData,
  closeCallback,
  reportId
) => async dispatch => {
  try {
    dispatch({ type: RECONCILE_SANTANDER_PAYMENT.start });

    await reconcileSantanderPayment({ paymentId, reconcileData });

    dispatch(
      showMessage({
        message: 'SANTANDER_REPORTS.RECONCILE_SUCCESS',
        variant: SnackBarVariants.SUCCESS
      })
    );

    dispatch(
      loadSantanderPaymentReportDetails({ santanderReportId: reportId })
    );

    closeCallback();

    return dispatch({ type: RECONCILE_SANTANDER_PAYMENT.success });
  } catch (error) {
    dispatch(
      showMessage({
        message: error.message,
        variant: SnackBarVariants.ERROR
      })
    );
    return dispatch({ type: RECONCILE_SANTANDER_PAYMENT.failure });
  }
};

export const createUpfrontRentFromSantanderPaymentAction = (
  paymentId,
  contractId,
  continueBilling,
  closeCallback,
  reportId
) => async dispatch => {
  try {
    dispatch({ type: CREATE_UPFRONT_RENT_SANTANDER.start });

    await createUpfrontRent({
      paymentId,
      contractId,
      continueBilling
    });

    dispatch(
      showMessage({
        message: 'SANTANDER_REPORTS.CREATE_UPFRONT_RENT_SUCCESS',
        variant: SnackBarVariants.SUCCESS
      })
    );

    dispatch(
      loadSantanderPaymentReportDetails({ santanderReportId: reportId })
    );

    closeCallback();

    return dispatch({ type: CREATE_UPFRONT_RENT_SANTANDER.success });
  } catch (error) {
    dispatch(
      showMessage({
        message: error.message,
        variant: SnackBarVariants.ERROR
      })
    );
    return dispatch({ type: CREATE_UPFRONT_RENT_SANTANDER.failure });
  }
};

export const downloadAccumulatedSantanderReferencedPaymentsAction = ({
  startDate,
  endDate
}) => async dispatch => {
  try {
    dispatch({ type: DOWNLOAD_SANTANDER_REFERENCED_PAYMENTS.start });
    await exportReferencedPaymentsReport({ startDate, endDate });
    return dispatch({ type: DOWNLOAD_SANTANDER_REFERENCED_PAYMENTS.success });
  } catch (error) {
    dispatch(
      showMessage({ message: error.message, variant: SnackBarVariants.ERROR })
    );
    return dispatch({ type: DOWNLOAD_SANTANDER_REFERENCED_PAYMENTS.failure });
  }
};

export const sendClabeToClientAction = (
  clientId,
  closeCallback
) => async dispatch => {
  try {
    dispatch({ type: SEND_CLABE_TO_CLIENT.start });

    await sendClabeToClient({ clientId });

    dispatch(
      showMessage({
        message: 'CLIENTS_VIEW.BILLING_DETAIL.SEND_BANK_INFO_SUCCESS',
        variant: SnackBarVariants.SUCCESS
      })
    );

    closeCallback();

    return dispatch({ type: SEND_CLABE_TO_CLIENT.success });
  } catch (error) {
    dispatch(
      showMessage({
        message: error.message,
        variant: SnackBarVariants.ERROR
      })
    );
    return dispatch({ type: SEND_CLABE_TO_CLIENT.failure });
  }
};
